import * as cs from "../../SignCommonStyled";
import {
  CustomError,
  Validate,
  ValidateMsg,
} from "api/interfaces/commonInterface.interface";
import { ValidateWord } from "components/atoms/text/Text";
import {
  SIGNUP_STEP,
  SIGNUP_UPDATE_TYPE,
  SignupUserInfo,
} from "api/interfaces/authInterface.interface";
import Password from "../common/Password";
import { postResetPassword } from "api/authAPI";
import useStatusMutation from "hook/useStatusMutation";
import useApiError from "hook/useApiError";
import React, { useCallback } from "react";
import { intialValidate } from "../SignupContainer";
import { checkPasswordNumberRegEx, checkPasswordRegEx } from "utils/regEx";

interface Props {
  onChangeUserInfo: (type: SIGNUP_UPDATE_TYPE, data: string | boolean) => void;
  userInfo: SignupUserInfo;
  validateInputMsg: ValidateMsg;
  isValidConfirm: boolean;
  isValidPassword: boolean;
  onClickBackLogin: () => void;
  passwordRef: any;
  confirmPasswordRef: any;
  validateMsg: string;
  isExpiredToken: boolean;
  setStep: React.Dispatch<React.SetStateAction<SIGNUP_STEP>>;
  setIsValidPassword: React.Dispatch<React.SetStateAction<boolean>>;
  setValidateMsg: React.Dispatch<React.SetStateAction<string>>;
  setIsValidated: React.Dispatch<React.SetStateAction<Validate>>;
  checkedEmail: string;
}

export default function ResetPassword(props: Props): JSX.Element {
  const {
    onChangeUserInfo,
    userInfo,
    validateInputMsg,
    isValidConfirm,
    isValidPassword,
    onClickBackLogin,
    passwordRef,
    confirmPasswordRef,
    validateMsg,
    isExpiredToken,
    setStep,
    setIsValidPassword,
    setValidateMsg,
    setIsValidated,
    checkedEmail,
  } = props;
  const { intlString, lang, displayError } = useApiError();
  const mutationResetPassword = useStatusMutation(postResetPassword, {
    onSuccess: (res: any) => {
      if (res.status === 200) {
        setStep(SIGNUP_STEP.RESET_PASSWORD_SUCCESS);
      } else {
        setIsValidPassword(true);
        // setValidateMsg("Change Password is Failure")
      }
    },
    onError: (error: CustomError) => {
      setValidateMsg(
        displayError(
          error,
          "Password reset unsuccessful.",
          "validateMsg.signup"
        )
      );
      setIsValidPassword(true);
      // setValidateMsg("Change Password is Failure")
    },
  });

  const onClickPassword = useCallback(() => {
    const { password, confirmPassword } = userInfo;
    setIsValidPassword(false);
    setIsValidated({
      ...intialValidate,
      password: true,
    });
    setValidateMsg("");

    if (
      password.length < 8 ||
      !checkPasswordRegEx(password) ||
      !checkPasswordNumberRegEx(password)
    ) {
      setIsValidated({
        ...intialValidate,
        confirmPassword: true,
      });
      setValidateMsg(
        intlString(lang.validateMsg.signup.reSetPassword.notRequired)
      );
      passwordRef.current?.focus();
      return true;
    }

    if (password !== confirmPassword) {
      setIsValidated({
        ...intialValidate,
        confirmPassword: true,
      });
      setValidateMsg(intlString(lang.validateMsg.signup.passwordMismatch));
      confirmPasswordRef.current?.focus();
      return true;
    }
    mutationResetPassword.mutate({
      payload: {
        email: checkedEmail,
        password: userInfo.password,
      },
    });
  }, [
    checkedEmail,
    confirmPasswordRef,
    intlString,
    lang,
    mutationResetPassword,
    passwordRef,
    setIsValidPassword,
    setIsValidated,
    setValidateMsg,
    userInfo,
  ]);

  return (
    <>
      <cs.InputWrapper>
        {!isExpiredToken && (
          <>
            <Password
              ref={passwordRef}
              validateInputMsg={validateInputMsg}
              onChangeUserInfo={onChangeUserInfo}
              userInfo={userInfo}
              label="Password"
              tabIndex={3}
              fieldKey="password"
              type={SIGNUP_UPDATE_TYPE.PASSWORD}
            />

            <Password
              ref={confirmPasswordRef}
              validateInputMsg={validateInputMsg}
              onChangeUserInfo={onChangeUserInfo}
              userInfo={userInfo}
              label="Confirm Password"
              tabIndex={4}
              fieldKey="confirmPassword"
              type={SIGNUP_UPDATE_TYPE.CONFIRM_PASSWORD}
              showRules={false}
              onEnter={() => onClickPassword()}
            />
          </>
        )}

        {isValidConfirm && (
          <cs.Bottom>
            <ValidateWord data-testid="auth-signup-reset-password-validate-mismatch">
              The password you entered does not match.
            </ValidateWord>
          </cs.Bottom>
        )}

        <cs.Bottom center={!isValidPassword}>
          <ValidateWord data-testid="auth-signup-reset-password-validate-password">
            {validateMsg || isValidPassword
              ? "Password reset unsuccessful."
              : ""}
          </ValidateWord>
        </cs.Bottom>
      </cs.InputWrapper>

      <div className="submit">
        {!isExpiredToken && (
          <cs.BigButton
            id="dw_signup_button_resetPassword"
            cancel={isExpiredToken}
            onClick={() => {
              if (!isExpiredToken) {
                onClickPassword();
              }
            }}
            data-testid="auth-reset-password-click-password"
          >
            Reset Password
          </cs.BigButton>
        )}

        {isExpiredToken && (
          <cs.BigButton
            id="dw_signup_button_forward_login"
            type="button"
            onClick={onClickBackLogin}
            data-testid="auth-signup-step-forward-login-click-back-login"
          >
            Return to Login Screen
          </cs.BigButton>
        )}
      </div>
    </>
  );
}
